import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SignaturePad from 'signature_pad';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  Copy,
  FileText,
  MapPin,
  Package,
  Pen,
  Printer,
  ShieldCheck,
  Timer,
  Truck,
  User,
  Wrench,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-PT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const PORTAL_LABEL_OVERRIDES = {
  client: 'Cliente',
  technician: 'Técnico',
  lead: 'Líder Técnico',
  supervisor: 'Supervisor',
};

const humanizeLabel = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  if (PORTAL_LABEL_OVERRIDES[normalized]) return PORTAL_LABEL_OVERRIDES[normalized];
  return normalized
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const PORTAL_DETAIL_LABELS = {
  vehicle_plate: 'Matrícula',
  vehicle_brand: 'Marca',
  vehicle_model: 'Modelo',
  fleet_number: 'Número da Frota',
  vehicle_type: 'Tipo de Viatura',
  operational_center: 'Centro Operacional',
  operation_type: 'Tipo de Operação',
  operation_other_description: 'Descrição manual',
  imei_gps: 'IMEI',
  serial_number: 'Número de Série',
  gps_equipment_type: 'Equipamento GPS',
  equipment_brand_model: 'Marca / Modelo do Equipamento',
  equipment_condition_before: 'Estado antes da intervenção',
  installation_location: 'Local de Instalação',
  installation_type: 'Tipo de Instalação',
  uninstallation_type: 'Campo de Desinstalação',
  communication_test: 'Teste de Comunicação',
  sim_card_number: 'Número SIM',
  intervention_reason: 'Motivo da intervenção',
  final_customer_department: 'Cliente final / Departamento',
  intervention_site: 'Local da intervenção',
  planned_date: 'Data planeada',
  priority: 'Prioridade',
  requires_travel: 'Requer deslocação',
  requires_final_tests: 'Requer testes finais',
};

const PORTAL_DETAIL_VALUE_LABELS = {
  installation: 'Instalação',
  reinstallation: 'Reinstalação',
  replacement: 'Substituição',
  vehicle_transfer: 'Transferência de Viatura',
  reconfiguration: 'Reconfiguração',
  uninstallation: 'Desinstalação',
  other: 'Outro',
  new_installation: 'Instalação Nova',
  transfer_installation: 'Instalação por Transferência',
  replacement_installation: 'Instalação por Substituição',
  maintenance_installation: 'Instalação em Manutenção',
  permanent_uninstallation: 'Desinstalação Definitiva',
  temporary_uninstallation: 'Desinstalação Temporária',
  transfer_uninstallation: 'Desinstalação para Transferência',
  replacement_uninstallation: 'Desinstalação para Substituição',
  fault_uninstallation: 'Desinstalação por Avaria',
  ok: 'OK',
  nok: 'NOK',
  pending: 'Pendente',
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  critical: 'Crítica',
  true: 'Sim',
  false: 'Não',
  client: 'Cliente',
  technician: 'Técnico',
  lead: 'Líder Técnico',
  supervisor: 'Supervisor',
};

const formatPortalDetailValue = (value) => {
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) return value.map((item) => formatPortalDetailValue(item)).join(', ');
  const normalized = String(value);
  return PORTAL_DETAIL_VALUE_LABELS[normalized] || normalized;
};

const parsePortalResponse = async (response, fallbackMessage) => {
  const rawText = await response.text();
  let data = {};

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = { detail: rawText };
    }
  }

  if (!response.ok) {
    throw new Error(data.detail || data.message || fallbackMessage);
  }

  return data;
};

export default function ProjectPortalPage() {
  const { token: accessToken } = useParams();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(initialEmail);
  const [signerName, setSignerName] = useState('');
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);

  useEffect(() => {
    if (!projectData || !canvasRef.current) return undefined;

    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = 220 * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    signaturePadRef.current = new SignaturePad(canvas, {
      penColor: '#0f172a',
      minWidth: 1,
      maxWidth: 2.5,
    });

    return () => {
      signaturePadRef.current?.off();
      signaturePadRef.current = null;
    };
  }, [projectData]);

  const loadProject = useCallback(async () => {
    if (!email.trim()) {
      setError('Indique o email autorizado para abrir o acompanhamento.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_URL}/api/projects/public/${accessToken}?email=${encodeURIComponent(email.trim())}`);
      const data = await parsePortalResponse(response, 'Erro ao carregar acompanhamento');
      setProjectData(data);
      setSignerName((current) => current || data.project?.customer_name || '');
    } catch (loadError) {
      setProjectData(null);
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, email]);

  useEffect(() => {
    if (initialEmail) {
      loadProject();
    }
  }, [initialEmail, loadProject]);

  const handleSign = async () => {
    const pad = signaturePadRef.current;
    if (!email.trim() || !signerName.trim()) {
      setError('Preencha o email e o nome do signatário.');
      return;
    }
    if (!pad || pad.isEmpty()) {
      setError('Desenhe a assinatura antes de continuar.');
      return;
    }

    setSigning(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/projects/public/${accessToken}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          signer_name: signerName.trim(),
          signature_data: pad.toDataURL(),
        }),
      });
      const data = await parsePortalResponse(response, 'Erro ao registar assinatura');
      await loadProject();
      setSuccess(data.message || 'Assinatura registada com sucesso.');
      signaturePadRef.current?.clear();
    } catch (signError) {
      setError(signError.message);
    } finally {
      setSigning(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!email.trim()) {
      setError('Indique o email autorizado para confirmar a recepção/entrega.');
      return;
    }

    setConfirmingDelivery(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/projects/public/${accessToken}/confirm-delivery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          confirmer_name: signerName.trim() || projectData?.project?.customer_name || '',
        }),
      });
      const data = await parsePortalResponse(response, 'Erro ao confirmar recepção/entrega');
      await loadProject();
      setSuccess(data.message || 'Recepção/entrega confirmada com sucesso.');
    } catch (confirmError) {
      setError(confirmError.message);
    } finally {
      setConfirmingDelivery(false);
    }
  };

  const project = projectData?.project;
  const execution = project?.execution_details || {};
  const publicSignatures = project?.signatures || [];

  const detailPairs = useMemo(() => {
    if (!project?.type_specific_details) return [];
    return Object.entries(project.type_specific_details)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        label: PORTAL_DETAIL_LABELS[key] || humanizeLabel(key),
        value: formatPortalDetailValue(value),
      }));
  }, [project?.type_specific_details]);

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 print:bg-white" data-testid="project-portal-page">
      <div className="mx-auto max-w-6xl space-y-6">
        <Card className="print:hidden" data-testid="project-portal-access-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Acompanhamento de Obra / Projecto
            </CardTitle>
            <CardDescription>
              Consulte os detalhes da obra, assine digitalmente, imprima o documento e confirme a recepção/entrega.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="project-portal-email-input">Email autorizado</Label>
                <Input
                  id="project-portal-email-input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="cliente@empresa.com"
                  data-testid="project-portal-email-input"
                />
              </div>
              <div>
                <Label htmlFor="project-portal-signer-name-input">Nome do signatário</Label>
                <Input
                  id="project-portal-signer-name-input"
                  value={signerName}
                  onChange={(event) => setSignerName(event.target.value)}
                  placeholder="Nome de quem assina"
                  data-testid="project-portal-signer-name-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={loadProject} disabled={loading} data-testid="project-portal-load-button">
                {loading ? 'A carregar...' : 'Abrir acompanhamento'}
              </Button>
              {project?.is_signed_by_client && (
                <Button variant="outline" onClick={() => window.print()} data-testid="project-portal-print-document-button">
                  <Printer className="mr-2 h-4 w-4" /> Imprimir documento
                </Button>
              )}
              {project?.is_signed_by_client && !project?.delivery_confirmed_by_client && (
                <Button variant="outline" onClick={handleConfirmDelivery} disabled={confirmingDelivery} data-testid="project-portal-confirm-delivery-button">
                  <Truck className="mr-2 h-4 w-4" /> {confirmingDelivery ? 'A confirmar...' : 'Confirmar recepção/entrega'}
                </Button>
              )}
            </div>

            {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="project-portal-error-alert">{error}</div>}
            {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="project-portal-success-alert">{success}</div>}
          </CardContent>
        </Card>

        {project && (
          <div className="space-y-6" data-testid="project-portal-document-view">
            <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
              <CardContent className="space-y-6 p-6 sm:p-8">
                <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <p className="text-sm font-medium uppercase tracking-[0.22em] text-slate-500">Documento de acompanhamento</p>
                    <h1 className="text-3xl font-semibold text-slate-950" data-testid="project-portal-project-title">{project.title}</h1>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-medium" data-testid="project-portal-project-number">{project.project_number}</span>
                      <Badge className="bg-slate-900 text-white" data-testid="project-portal-status-badge">{project.status_label || project.status}</Badge>
                      {project.delivery_confirmed_by_client && (
                        <Badge className="bg-emerald-100 text-emerald-800" data-testid="project-portal-delivery-confirmed-badge">
                          Recepção/entrega confirmada
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Cliente</p>
                      <p className="mt-1 font-medium text-slate-900">{project.customer_name || '—'}</p>
                      <p>{project.customer_phone || project.customer_email || '—'}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Local / Loja</p>
                      <p className="mt-1 font-medium text-slate-900">{project.work_location || project.store_name || '—'}</p>
                      <p>{project.work_city || project.work_address || '—'}</p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="project-portal-start-time-card">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Timer className="h-4 w-4" /> Hora inicial</div>
                    <p className="mt-2 text-lg font-semibold text-slate-950">{execution.start_time || '—'}</p>
                    <p className="text-xs text-slate-500">Data inicial: {execution.execution_date || '—'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="project-portal-end-time-card">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Clock className="h-4 w-4" /> Hora final</div>
                    <p className="mt-2 text-lg font-semibold text-slate-950">{execution.end_time || '—'}</p>
                    <p className="text-xs text-slate-500">Data final: {execution.execution_end_date || execution.execution_date || '—'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="project-portal-technician-card">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Wrench className="h-4 w-4" /> Técnico(s)</div>
                    <p className="mt-2 text-lg font-semibold text-slate-950">{execution.technicians || project.technician_name || '—'}</p>
                    <p className="text-xs text-slate-500">Execução da obra</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="project-portal-checklist-progress-card">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><ClipboardList className="h-4 w-4" /> Checklist</div>
                    <p className="mt-2 text-lg font-semibold text-slate-950">{project.checklist_progress?.percent || 0}%</p>
                    <p className="text-xs text-slate-500">{project.checklist_progress?.completed || 0}/{project.checklist_progress?.total || 0} itens concluídos</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="project-portal-duration-card">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Calendar className="h-4 w-4" /> Duração total</div>
                    <p className="mt-2 text-lg font-semibold text-slate-950">{execution.duration_str || '—'}</p>
                    <p className="text-xs text-slate-500">Calculada com data/hora inicial e final</p>
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1.3fr_0.9fr]">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base"><FileText className="h-4 w-4" /> Detalhes da Obra</CardTitle>
                      <CardDescription>Informações operacionais e técnicas visíveis para acompanhamento e impressão.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm text-slate-700">
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Descrição inicial</p>
                        <p className="mt-1 whitespace-pre-wrap" data-testid="project-portal-description-value">{project.description || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Descrição técnica</p>
                        <p className="mt-1 whitespace-pre-wrap" data-testid="project-portal-technical-description-value">{project.technical_description || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Escopo / âmbito do trabalho</p>
                        <p className="mt-1 whitespace-pre-wrap" data-testid="project-portal-scope-value">{project.scope_of_work || '—'}</p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 px-3 py-3"><MapPin className="mb-2 h-4 w-4 text-slate-500" /><p className="font-medium text-slate-900">Morada / Cidade</p><p>{project.work_address || '—'}</p><p>{project.work_city || '—'}</p></div>
                        <div className="rounded-xl border border-slate-200 px-3 py-3"><Calendar className="mb-2 h-4 w-4 text-slate-500" /><p className="font-medium text-slate-900">Criado em</p><p>{formatDateTime(project.created_at)}</p><p>Coordenadas: {project.gps_coordinates || '—'}</p></div>
                      </div>

                      {detailPairs.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Detalhes específicos da instalação</p>
                          <div className="mt-2 grid gap-3 sm:grid-cols-2">
                            {detailPairs.map((detail) => (
                              <div key={detail.key} className="rounded-xl border border-slate-200 px-3 py-3" data-testid={`project-portal-type-detail-${detail.key}`}>
                                <p className="text-xs uppercase tracking-[0.15em] text-slate-400">{detail.label}</p>
                                <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{detail.value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4" /> Execução & recepção</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-slate-700">
                      <div className="rounded-xl border border-slate-200 px-3 py-3" data-testid="project-portal-execution-date-value">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Data de execução inicial</p>
                        <p className="mt-1 font-medium text-slate-900">{execution.execution_date || '—'}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 px-3 py-3" data-testid="project-portal-execution-end-date-value">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Data de execução final</p>
                        <p className="mt-1 font-medium text-slate-900">{execution.execution_end_date || execution.execution_date || '—'}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 px-3 py-3" data-testid="project-portal-work-order-value">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Ordem de trabalho</p>
                        <p className="mt-1 font-medium text-slate-900">{execution.work_order_ref || '—'}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 px-3 py-3" data-testid="project-portal-final-observations-value">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Observações finais</p>
                        <p className="mt-1 whitespace-pre-wrap">{execution.final_observations || '—'}</p>
                      </div>
                      {execution.work_segments?.length > 0 && (
                        <div className="rounded-xl border border-slate-200 px-3 py-3" data-testid="project-portal-effective-work-segments">
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Jornadas efectivas consideradas</p>
                          <div className="mt-2 space-y-2">
                            {execution.work_segments.map((segment, index) => (
                              <div key={`${segment.work_date}-${segment.start_time}-${index}`} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                <p className="font-medium text-slate-900">{segment.work_date || '—'}</p>
                                <p>{segment.start_time || '—'} → {segment.end_time || '—'}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {project.delivery_confirmed_by_client && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3" data-testid="project-portal-delivery-confirmation-card">
                          <p className="font-medium text-emerald-900">Recepção/entrega confirmada</p>
                          <p className="text-sm text-emerald-800">{project.delivery_confirmed_by_name || 'Cliente'} em {formatDateTime(project.delivery_confirmed_at)}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {project.materials?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base"><Package className="h-4 w-4" /> Materiais aplicados</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {project.materials.map((material) => (
                          <div key={material.id || `${material.description}-${material.serial_number || ''}`} className="rounded-xl border border-slate-200 px-4 py-3 text-sm" data-testid={`project-portal-material-${material.id || material.description}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="font-medium text-slate-900">{material.description || 'Material'}</p>
                              <span className="text-slate-500">Qtd: {material.quantity || '—'} {material.unit || ''}</span>
                            </div>
                            {(material.serial_number || material.notes) && (
                              <p className="mt-1 text-slate-600 whitespace-pre-wrap">
                                {[material.serial_number ? `S/N: ${material.serial_number}` : null, material.notes].filter(Boolean).join(' • ')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {project.additional_service_interventions?.length > 0 && (
                  <Card data-testid="project-portal-additional-interventions-section">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base"><Copy className="h-4 w-4" /> Histórico de intervenções complementares</CardTitle>
                      <CardDescription>
                        Registo dos serviços adicionais efectuados após a intervenção inicial, com data, horário, técnico, venda complementar e notas completas.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {project.additional_service_interventions.map((entry) => (
                        <div key={entry.id || `${entry.sale_number}-${entry.intervention_date}`} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm print:break-inside-avoid" data-testid={`project-portal-additional-intervention-${entry.id || entry.sale_number}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-medium text-slate-900">{entry.description || 'Serviço complementar'}</p>
                            {entry.sale_number && <Badge variant="outline">Venda complementar: {entry.sale_number}</Badge>}
                          </div>
                          <div className="mt-2 grid gap-2 text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                            <p><strong>Data:</strong> {entry.intervention_date || '—'}</p>
                            <p><strong>Hora inicial:</strong> {entry.start_time || '—'}</p>
                            <p><strong>Hora final:</strong> {entry.end_time || '—'}</p>
                            <p><strong>Técnico:</strong> {entry.technician_name || '—'}</p>
                          </div>
                          {entry.reason && <p className="mt-2 text-slate-700"><strong>Motivo:</strong> {entry.reason}</p>}
                          {entry.notes && <p className="mt-2 whitespace-pre-wrap text-slate-700"><strong>Notas:</strong> {entry.notes}</p>}
                          <p className="mt-2 text-xs text-slate-500">Registado em {formatDateTime(entry.created_at)} por {entry.created_by_name || 'Equipa técnica'}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {project.checklist_items?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base"><CheckCircle className="h-4 w-4" /> Checklist da obra</CardTitle>
                      <CardDescription>
                        Itens concluídos e pendentes visíveis no documento e na impressão.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-3 md:grid-cols-2">
                      {project.checklist_items.map((item, index) => {
                        const itemKey = item.id || item.code || `checklist-${index}`;
                        const itemLabel = item.label || item.description || item.title || `Item ${index + 1}`;
                        return (
                        <div key={itemKey} className={`rounded-xl border px-4 py-3 text-sm print:break-inside-avoid ${item.is_completed ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid={`project-portal-checklist-item-${itemKey}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-slate-900">{itemLabel}</p>
                            <Badge className={item.is_completed ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'}>
                              {item.is_completed ? 'Concluído' : 'Pendente'}
                            </Badge>
                          </div>
                          {(item.notes || item.completed_by || item.completed_at) && (
                            <div className="mt-2 space-y-1 text-slate-600">
                              {item.notes && <p className="whitespace-pre-wrap">{item.notes}</p>}
                              {item.completed_by && <p className="text-xs">Executado por: {item.completed_by}</p>}
                              {item.completed_at && <p className="text-xs">Concluído em: {formatDateTime(item.completed_at)}</p>}
                            </div>
                          )}
                        </div>
                      )})}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><Pen className="h-4 w-4" /> Assinaturas</CardTitle>
                    <CardDescription>
                      O cliente pode assinar digitalmente. Após a assinatura, o documento fica pronto para impressão.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 p-4" data-testid="project-portal-client-signature-status-card">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Assinatura do cliente</p>
                        <p className="mt-2 text-sm font-medium text-slate-900">{project.client_signer_name || 'Ainda não assinada'}</p>
                        <p className="text-sm text-slate-500">{formatDateTime(project.client_signed_at)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 p-4" data-testid="project-portal-technician-signature-status-card">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Assinatura técnica</p>
                        <p className="mt-2 text-sm font-medium text-slate-900">{project.technician_signer_name || 'Ainda não assinada'}</p>
                        <p className="text-sm text-slate-500">{formatDateTime(project.technician_signed_at)}</p>
                      </div>
                    </div>

                    {publicSignatures.length > 0 && (
                      <div className="grid gap-4 lg:grid-cols-2">
                        {publicSignatures.map((signature) => (
                          <div key={signature.id || signature.signer_role} className="rounded-2xl border border-slate-200 p-4" data-testid={`project-portal-saved-signature-${signature.signer_role}`}>
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{humanizeLabel(signature.signer_role)}</p>
                            <p className="mt-1 font-medium text-slate-900">{signature.signer_name}</p>
                            <p className="mb-3 text-xs text-slate-500">{formatDateTime(signature.signed_at)}</p>
                            {signature.signature_data && (
                              <img src={signature.signature_data} alt={`Assinatura ${signature.signer_role}`} className="h-28 w-full rounded-xl border border-slate-200 object-contain bg-white" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="rounded-2xl border border-dashed border-slate-300 p-4 print:hidden">
                      <Label className="mb-2 block">Assinatura do cliente</Label>
                      <canvas ref={canvasRef} className="h-[220px] w-full rounded-xl border border-slate-200 bg-white" data-testid="project-portal-signature-canvas" />
                      <div className="mt-3 flex flex-wrap gap-3">
                        <Button variant="outline" onClick={() => signaturePadRef.current?.clear()} data-testid="project-portal-clear-signature-button">
                          Limpar desenho
                        </Button>
                        <Button onClick={handleSign} disabled={signing} data-testid="project-portal-submit-signature-button">
                          {signing ? 'A assinar...' : 'Assinar documento'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}